import React, { useState, useEffect } from "react";
import { examinationLinksApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./ExaminationLinks.scss";

const ExaminationLinks = () => {
  const [links, setLinks] = useState([]);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [editIndex, setEditIndex] = useState(-1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await apiClient(examinationLinksApi);
      const result = await response.json();
      if (response.ok && result.data && result.data.links) {
        setLinks(result.data.links);
      } else {
        setLinks([]);
      }
    } catch (error) {
      console.error("Error fetching examination links:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveToBackend = async (updatedLinks) => {
    try {
      setLoading(true);
      const response = await apiClient(examinationLinksApi, {
        method: "PUT",
        body: JSON.stringify({ links: updatedLinks }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        alert(`Failed to save links. Error ${response.status}: ${errorText}`);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error saving examination links:", error);
      alert("Error saving examination links.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) return;

    let updatedLinks;
    if (editIndex >= 0) {
      updatedLinks = [...links];
      updatedLinks[editIndex] = { title, url };
    } else {
      updatedLinks = [...links, { title, url }];
    }

    const success = await saveToBackend(updatedLinks);
    if (success) {
      setLinks(updatedLinks);
      setTitle("");
      setUrl("");
      setEditIndex(-1);
    }
  };

  const handleEdit = (index) => {
    setTitle(links[index].title);
    setUrl(links[index].url);
    setEditIndex(index);
  };

  const handleDelete = async (index) => {
    if (!window.confirm("Are you sure you want to delete this link?")) return;
    const updatedLinks = links.filter((_, i) => i !== index);
    const success = await saveToBackend(updatedLinks);
    if (success) {
      setLinks(updatedLinks);
    }
  };

  const handleCancelEdit = () => {
    setTitle("");
    setUrl("");
    setEditIndex(-1);
  };

  return (
    <div className="el-page">
      <div className="el-header">
        <div className="el-header-left">
          <div className="el-header-icon">🔗</div>
          <div>
            <div className="el-breadcrumb">
              <span>Examination</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="el-bc-active">Examination Links</span>
            </div>
            <h1>Examination Links</h1>
            <p>Manage dynamic external URLs for the examination submenus on the college website.</p>
          </div>
        </div>
      </div>

      <div className="el-card">
        <form className="el-form" onSubmit={handleAddOrUpdate}>
          <div className="el-links-container">
            <h3 className="section-title">{editIndex >= 0 ? "Edit Link" : "Add New Link"}</h3>
            <div className="el-link-row">
              <div className="form-group full el-link-title">
                <label>Topic or Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Even Semester Admit Card 2025-26"
                  required
                />
              </div>
              <div className="form-group full el-link-url">
                <label>Enter URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="e.g. https://lucknow.univ.edu/admit-card"
                  required
                />
              </div>
            </div>
          </div>

          <div className="el-form-footer">
            {editIndex >= 0 && (
              <button type="button" className="btn btn-secondary mr-2" onClick={handleCancelEdit}>
                Cancel
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (editIndex >= 0 ? "Update Link" : "Add Link")}
            </button>
          </div>
        </form>
      </div>

      {links.length > 0 && (
        <div className="el-card mt-4">
          <h3 className="section-title">All Links</h3>
          <div className="table-responsive">
            <table className="el-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Topic or Title</th>
                  <th>URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{link.title}</td>
                    <td>
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.url}
                      </a>
                    </td>
                    <td>
                      <button 
                        className="btn-icon edit" 
                        onClick={() => handleEdit(index)}
                        title="Edit"
                        disabled={loading}
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-icon delete" 
                        onClick={() => handleDelete(index)}
                        title="Delete"
                        disabled={loading}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExaminationLinks;
